// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useRef, useState } from 'react';

import { CanceledError, type AxiosRequestConfig } from 'axios';

import type { StrongOmit, WithRequired } from './_helpers/helper-types';
import { isCanceledApiRequest, ignoreCanceledApiRequest } from './_helpers/isRequestError';
import { useTrackedRequests } from './_helpers/useTrackedRequests';
import type { AdditionalRequestConfig } from './_helpers/wrapped_api_call/external-types';
import {
  useWrappedApiCallNoBody,
  type Opts,
  type NoBodyApiCallArgs,
  type ApiCallReturn,
} from './_helpers/wrapped_api_call/useWrappedApiCall';

import type {
  EvolveApiBody,
  EvolveApiParams,
  EvolveApiResponse,
  EvolveApiRoute,
  RequiredRoute,
  RouteWithPathParam,
} from './ENDPOINTS';

type BasePaginatedOpts<Route extends EvolveApiRoute<'GET_PAGINATED'>> = Route extends RouteWithPathParam
  ? WithRequired<Pick<Opts<'GET_PAGINATED', Route>, 'defaultConfig'>, 'defaultConfig'>
  : Pick<Opts<'GET_PAGINATED', Route>, 'defaultConfig'>;

export type GetPaginatedOpts<Route extends EvolveApiRoute<'GET_PAGINATED'>> = BasePaginatedOpts<Route> & {
  /** How many results to return per call. */
  perPage?: number;
};

export type EvolvePaginatedResponse<DataType> = {
  data: DataType[];
  requestedSkip: number;
  requestedTake: number;
  entireCount: number;
};

type SingleOrderBy = `${string}:${'asc' | 'desc'}`;
export type OrderBy = SingleOrderBy | SingleOrderBy[];

export type PageFetcherArgs = {
  skip?: number;
  take?: number;
  searchPhrase?: string;
  orderBy?: OrderBy | OrderBy[];
};

export type PageFetcher<Route extends EvolveApiRoute<'GET_PAGINATED'>> = (
  args: PageFetcherArgs,
  config: AdditionalRequestConfig<'GET_PAGINATED', Route>,
) => Promise<EvolveApiResponse<'GET_PAGINATED', Route>>;

export type WrappedPaginatedGet<Route extends EvolveApiRoute<'GET_PAGINATED'>> = {
  /** Used to update the default opts after hook instantiation. */
  setDefaultOpts: (
    defaultOpts: GetPaginatedOpts<Route>,
    /**
     * Whether or not to call `reset()` in preparation for a new call.
     *
     * @remarks If not set, will call `reset()` if the new `defaultOpts` differ from the previous one. Checks using shallow comparison.
     */
    resetData?: boolean,
  ) => void;
  /**
   * Used to fetch a specific page of data.
   */
  fetchPage: PageFetcher<Route>;
  /**
   * Used to fetch the next available page of data.
   *
   * Calling this will update the `data` variable.
   */
  fetchNextPage: (
    config?: Partial<
      Omit<AxiosRequestConfig<EvolveApiBody<'GET_PAGINATED', Route>>, 'url' | 'params'> & {
        params?: Partial<EvolveApiParams<'GET_PAGINATED', Route>>;
      }
    >,
  ) => Promise<boolean>;
  /** Clears out all stored data (usually in prep for a new `fetchNextPage`) */
  reset: (refresh?: boolean) => void;
  /** The returned data from this API call (if `fetchNextPage` has been called). */
  data: EvolveApiResponse<'GET_PAGINATED', Route>['data'];
  /**
   * The total number of records available on the backend.
   * Only populated if at least one page has been fetched.
   */
  entireCount: number | undefined;
  /** Whether the API call is currently in progress. */
  loading: boolean;
  /** Used to add a search to the API call. */
  searchHandler: (searchPhrase: string) => void;
  /** Used to add a sort to the API call. */
  sortHandler: (sort: OrderBy) => void;
  /** Any errors returned from the backend. */
  errors: unknown | undefined;
  /** Cancels any in-progress API calls. Calls made afterwards are unaffected. */
  abortAll: (reason: string) => void;
};

/**
 * Wrapper for Evolve's pagination API, used with `Multisearch` endpoints.
 */
export const useEvolvePaginatedGet = <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  url: Route,
  initialOpts?: GetPaginatedOpts<Route>,
): WrappedPaginatedGet<Route> => {
  const [searchPhrase, setSearchPhrase] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>();
  const [defaultOpts, setDefaultOptsInner] = useState(initialOpts);

  const {
    data: _1,
    setDefaultOpts: _2,
    reset: _3,
    loading,
    apiCall,
    abortAll,
    ...rest
  } = useWrappedApiCallNoBody<'GET_PAGINATED', Route>('get', url);
  // This guard prevents overlapping API calls that would cause duplicate pages.
  // Only the first request is allowed until the older in-flight API call goes through.
  // Other calls to `fetchNextPage` will be ignored until the current one completes.
  // This differs from the `abortOnMoreRecent` behavior, which cancels older requests in favor of newer ones.
  const nextPageInFlightGuardRef = useRef(false);
  // Used to prevent rapid calling if we're receiving errors
  const errorCountRef = useRef(0);
  const hasMoreRef = useRef(true);
  const totalRequestedRef = useRef(0);
  // Used to make sure we don't override a newer call when an older call returns
  const numCallsRef = useRef(0);

  // `data` above only has the most recent page,
  // so we keep track of all of the concatenated pages here
  const [fullData, setFullData] = useState<WrappedPaginatedGet<Route>['data']>([]);
  const [entireCount, setEntireCount] = useState<number | undefined>();

  /**
   * Used to fetch an individual page.
   *
   * **Note:** Will not update the returned `data` from this hook.
   */
  const fetchPage = useCallback<PageFetcher<Route>>(
    async (
      {
        skip,
        // Default to 50 per page if none provided
        take = defaultOpts?.perPage ?? 50,
        searchPhrase: searchPhraseOverride = searchPhrase,
        orderBy: orderByOverride = orderBy,
      },
      overrideConfig,
    ) => {
      const config = {
        ...defaultOpts?.defaultConfig,
        ...overrideConfig,
        params: {
          ...defaultOpts?.defaultConfig?.params,
          skip,
          take,
          ...(orderByOverride && orderByOverride.length > 0 ? { orderBy: orderByOverride } : {}),
          ...(searchPhraseOverride ? { searchPhrase: searchPhraseOverride } : {}),
          ...overrideConfig.params,
        },
      } as AdditionalRequestConfig<'GET_PAGINATED', Route>;

      // @ts-expect-error TS can't know that the above `config` actually satisfies GetApiCallArgs due to the ternary
      const args = [config] as NoBodyApiCallArgs<Route>;
      return apiCall(...args)
        .then((res) => {
          setEntireCount(res.entireCount);
          return res;
        })
        .catch((e) => {
          if (!isCanceledApiRequest(e)) {
            errorCountRef.current += 1;
          }

          throw e;
        });
    },
    [apiCall, defaultOpts?.defaultConfig, defaultOpts?.perPage, orderBy, searchPhrase],
  );

  /**
   * Used to fetch the next page of data.
   * To access the data itself, use the `data` field returned from the hook.
   * @returns a boolean indicating whether or not there is still additional data
   */
  const fetchNextPage = useCallback<WrappedPaginatedGet<Route>['fetchNextPage']>(
    async (config) => {
      const numCall = numCallsRef.current;
      // If we're actively loading the next page,
      // or we've retrieved all available data,
      // don't bother sending another request.
      if (nextPageInFlightGuardRef.current || !hasMoreRef.current) {
        return hasMoreRef.current;
      }
      nextPageInFlightGuardRef.current = true;
      try {
        const res = await fetchPage(
          { skip: totalRequestedRef.current },
          (config ?? {}) as AdditionalRequestConfig<'GET_PAGINATED', Route>,
        );
        if (res && numCall === numCallsRef.current) {
          setFullData(
            (alreadyFetched) => [...alreadyFetched, ...res.data] as EvolveApiResponse<'GET_PAGINATED', Route>['data'],
          );
          const total = Math.max(res.requestedTake, res.data.length) + res.requestedSkip;
          totalRequestedRef.current = total;
          hasMoreRef.current = total < res.entireCount;
        }
        return hasMoreRef.current;
      } finally {
        if (errorCountRef.current > 5) {
          // if we've errored too many times, stop calling
          hasMoreRef.current = false;
        }
        if (numCall === numCallsRef.current) {
          nextPageInFlightGuardRef.current = false;
        }
      }
    },
    [fetchPage],
  );

  const [needsRefresh, setNeedsRefresh] = useState(false);
  const reset = useCallback(
    (refresh = true) => {
      numCallsRef.current += 1;
      errorCountRef.current = 0;
      nextPageInFlightGuardRef.current = false;
      hasMoreRef.current = true;
      abortAll('API call & data reset');
      setFullData([]);
      setEntireCount(undefined);
      setNeedsRefresh(
        refresh &&
          // Only refresh the data if we had data to begin with
          totalRequestedRef.current > 0,
      );
      totalRequestedRef.current = 0;
    },
    [abortAll],
  );

  useEffect(() => {
    if (needsRefresh) {
      void fetchNextPage();
      setNeedsRefresh(false);
    }
  }, [fetchNextPage, needsRefresh]);

  const setDefaultOpts = useCallback<WrappedPaginatedGet<Route>['setDefaultOpts']>(
    (newOpts, resetData) => {
      setDefaultOptsInner((opts) => {
        const shouldUpdate = opts !== newOpts;
        // If the user explicitly passed in `resetData`,
        // always use that value to determine if we should reset.
        // If they didn't, only reset if the data changed,
        // avoiding unnecessary network calls.
        if (resetData ?? shouldUpdate) {
          reset(false);
        }
        return shouldUpdate ? newOpts : opts;
      });
    },
    [reset],
  );

  const searchHandler = useCallback(
    (value: string) => {
      setSearchPhrase(value.trim());
      reset(false);
    },
    [reset],
  );
  const sortHandler = useCallback(
    (value: OrderBy) => {
      const valueWithoutSpaces = (Array.isArray(value) ? value : [value]).map((v) =>
        v.replaceAll(/\s/g, '').toLowerCase(),
      ) as OrderBy;
      setOrderBy(valueWithoutSpaces);
      reset(false);
    },
    [reset],
  );

  return {
    setDefaultOpts,
    fetchPage,
    fetchNextPage,
    reset,
    data: fullData,
    entireCount,
    searchHandler,
    sortHandler,
    loading,
    abortAll,
    ...rest,
  };
};

/** Larger than the paginated default, since every page is fetched anyway. */
const GET_ALL_PAGE_SIZE = 200;

/**
 * Options for the `useEvolvePaginatedGetAll` hook.
 */
export type GetPaginatedAllOpts<Route extends EvolveApiRoute<'GET_PAGINATED'>> = BasePaginatedOpts<Route> & {
  /**
   * If false, will automatically fetch every page on load and on every config change.
   * `apiCall` always fetches, regardless of this value.
   *
   * @defaultValue true
   */
  lazy?: boolean;
};

/**
 * The return type of the `useEvolvePaginatedGetAll` hook
 * It intentionally resembles the return type of `useEvolveGet`, since it's meant to make paginated endpoints behave like non-paginated ones.
 */
export type WrappedPaginatedGetAll<Route extends EvolveApiRoute<'GET_PAGINATED'>> = {
  /** Every record from the most recent `apiCall` that completed. */
  data: EvolveApiResponse<'GET_PAGINATED', Route>['data'] | undefined;
  /**
   * Fetches every page, stores the records in `data`, and resolves with them.
   * Cancels any fetch already in progress.
   */
  apiCall: (
    config?: Partial<StrongOmit<AxiosRequestConfig<EvolveApiBody<'GET_PAGINATED', Route>>, 'url' | 'params'>> & {
      url?: RequiredRoute<'GET_PAGINATED', Route>;
      params?: Partial<EvolveApiParams<'GET_PAGINATED', Route>>;
    },
  ) => Promise<EvolveApiResponse<'GET_PAGINATED', Route>['data']>;
  /** Used to update the default opts after hook instantiation. */
  setDefaultOpts: (opts: GetPaginatedAllOpts<Route>) => void;
} & StrongOmit<
  ApiCallReturn<'GET_PAGINATED', Route, NoBodyApiCallArgs<'GET_PAGINATED', Route>>,
  'apiCall' | 'setDefaultOpts' | 'data'
>;

/**
 * Used to get every record from a paginated EVOLVE API route as a single array.
 *
 * Generally, this indicates a bad practice, and the design
 * should be reworked to avoid this when possible.
 */
export const useEvolvePaginatedGetAll = <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  initialOpts?: GetPaginatedAllOpts<Route>,
): WrappedPaginatedGetAll<Route> => {
  const [defaultOpts, setDefaultOptsInner] = useState(initialOpts);
  const [lazy, setLazy] = useState(initialOpts?.lazy ?? true);
  // The `apiCall` the automatic fetch last started with, so a render before the effect below re-runs still reads as loading.
  const [autoFetchedWith, setAutoFetchedWith] = useState<{ apiCall: WrappedPaginatedGetAll<Route>['apiCall'] } | null>(
    null,
  );
  const [data, setData] = useState<EvolveApiResponse<'GET_PAGINATED', Route>['data'] | undefined>(undefined);
  const {
    apiCall: fetchPage,
    abortAll: abortPages,
    errors,
  } = useWrappedApiCallNoBody<'GET_PAGINATED', Route>('get', url);
  const { loading, abortAll, trackRequest } = useTrackedRequests();

  const apiCall = useCallback<WrappedPaginatedGetAll<Route>['apiCall']>(
    (config) =>
      trackRequest(
        async (signal) => {
          // Aborting the fetch also cancels whichever page is in flight.

          signal.addEventListener(
            'abort',
            () => {
              // `signal.reason` is typed as `any`. It *should* be a `CanceledError`, but type it as unknown as a precaution.
              const reason = signal.reason as unknown;
              abortPages(reason instanceof CanceledError ? reason.message : 'Request aborted');
            },
            {
              once: true,
            },
          );
          let allData: EvolveApiResponse<'GET_PAGINATED', Route>['data'] = [];
          let page: EvolveApiResponse<'GET_PAGINATED', Route>;
          do {
            const pageConfig = {
              ...defaultOpts?.defaultConfig,
              ...config,
              params: {
                ...defaultOpts?.defaultConfig?.params,
                ...config?.params,
                skip: allData.length,
                take: GET_ALL_PAGE_SIZE,
              },
            } as AdditionalRequestConfig<'GET_PAGINATED', Route>;
            // @ts-expect-error TS can't know that the above `config` actually satisfies NoBodyApiCallArgs due to the ternary
            page = await fetchPage(pageConfig);
            // `trackRequests` handles this, but only after the whole promise is resolved, so we check if the signal was aborted
            // here so that we don't start fetching pages we don't need.
            signal.throwIfAborted();
            // Spreading widens the route's array type to an array of every route's records; both come from this route.
            allData = [...allData, ...page.data] as typeof allData;
            // An empty page means records were removed mid-fetch; stop rather than request the same skip forever.
          } while (page.data.length > 0 && allData.length < page.entireCount);
          setData(allData);
          return allData;
        },
        true,
        `More recent call made. ${config?.url ?? url}`,
      ),
    [abortPages, defaultOpts?.defaultConfig, fetchPage, trackRequest, url],
  );

  useEffect(() => {
    if (lazy) return undefined;
    setAutoFetchedWith({ apiCall });
    void apiCall().catch(ignoreCanceledApiRequest);
    return () => abortAll('Automatic fetch replaced or disabled.');
  }, [abortAll, apiCall, lazy]);

  const autoFetchPending = !lazy && autoFetchedWith?.apiCall !== apiCall;

  const reset = useCallback(() => {
    abortAll('API call & data reset');
    setData(undefined);
  }, [abortAll]);

  const setDefaultOpts = useCallback((newOpts: GetPaginatedAllOpts<Route>) => {
    setDefaultOptsInner(newOpts);
    setLazy((l) => newOpts.lazy ?? l);
  }, []);

  return {
    data,
    loading: loading || autoFetchPending,
    apiCall,
    setDefaultOpts,
    errors,
    abortAll,
    reset,
  };
};
